import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { ArrowUpTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const fetchFiles = () => api.get('/reconciliation/files').then(res => res.data.data);

export default function Reconciliation() {
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const { data: filesData, isLoading } = useQuery('reconciliation-files', fetchFiles);
  const files = filesData?.files || [];

  const uploadFile = useMutation(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/reconciliation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    {
      onMutate: () => setUploading(true),
      onSuccess: () => {
        queryClient.invalidateQueries('reconciliation-files');
        toast.success('File uploaded and processing started');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Upload failed'),
      onSettled: () => setUploading(false)
    }
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) uploadFile.mutate(file);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reconciliation</h1>
          <p className="mt-1 text-sm text-slate-500">Match bank MIS with system transactions</p>
        </div>
        <label className="btn-primary cursor-pointer">
          <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading...' : 'Upload MIS File'}
          <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} disabled={uploading} />
        </label>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Date</th>
                <th>Total Records</th>
                <th>Matched</th>
                <th>Unmatched</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isLoading ? (
                <tr><td colSpan="6" className="text-center py-8">Loading...</td></tr>
              ) : files.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-slate-500">No reconciliation files uploaded</td></tr>
              ) : (
                files.map((file) => (
                  <tr key={file.id} className="hover:bg-slate-50">
                    <td className="font-medium">{file.fileName}</td>
                    <td>{new Date(file.createdAt).toLocaleDateString()}</td>
                    <td>{file.totalRecords}</td>
                    <td className="text-green-600 font-medium">{file.matchedRecords}</td>
                    <td className="text-red-600 font-medium">{file.unmatchedRecords}</td>
                    <td>
                      <span className={`badge ${file.status === 'completed' ? 'badge-green' :
                          file.status === 'processing' ? 'badge-yellow' :
                            file.status === 'failed' ? 'badge-red' : 'badge-gray'
                        }`}>
                        {file.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
